"use client";

import { useEffect, useSyncExternalStore } from "react";

import { glassToast } from "@/components/ui/glass-toast";
import { useOptionalNotifications } from "@/components/notifications/notification-context";
import {
  isBackendOnline,
  subscribeToBackendOnline,
} from "@/lib/pwa/connectivity";

const TOAST_ID = "network-offline";
const ALERT_KEY = "network-offline";
const getServerSnapshot = () => true;

// Both mounts (root layout + app shell) share one toast id, so sonner renders
// a single toast. Ref-count the active mounts so the shell mount unmounting on
// a route change can't dismiss the toast the root mount still owns.
let activeOfflineMounts = 0;

/**
 * Offline indicator. Mounted TWICE on purpose:
 *
 * - app/layout.tsx (root) so /auth/login, marketing and every other public
 *   route gets offline feedback. There is no NotificationProvider that high in
 *   the tree, so the system-alert half is skipped there.
 * - GlobalToastControllers inside the authenticated shell, where the provider
 *   exists and the notification drawer's system alert is registered as before.
 *
 * The two halves are independent: the glass toast only needs the root
 * `<Toaster />`, the system alert needs the notification context. Both mounts
 * share the same toast id, so sonner renders exactly one toast either way.
 */
export function NetworkStatusToastController() {
  const isOnline = useSyncExternalStore(
    subscribeToBackendOnline,
    isBackendOnline,
    getServerSnapshot,
  );
  const notifications = useOptionalNotifications();
  const registerSystemAlert = notifications?.registerSystemAlert;
  const unregisterSystemAlert = notifications?.unregisterSystemAlert;

  useEffect(() => {
    if (isOnline) return;

    activeOfflineMounts += 1;
    glassToast.persistent({
      id: TOAST_ID,
      variant: "rose",
      title: "You’re offline",
      body: "Contractor updates and team deployments will queue. Other actions need a connection.",
    });
    registerSystemAlert?.({
      key: ALERT_KEY,
      priority: "warning",
      title: "You’re offline",
      body: "Network connection unavailable.",
    });

    return () => {
      activeOfflineMounts -= 1;
      // Only the last controller standing clears the shared toast.
      if (activeOfflineMounts <= 0) {
        activeOfflineMounts = 0;
        glassToast.dismiss(TOAST_ID);
      }
      unregisterSystemAlert?.(ALERT_KEY);
    };
  }, [isOnline, registerSystemAlert, unregisterSystemAlert]);

  return null;
}
